import { Component, OnInit, OnDestroy } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { Ticket, CartProduct } from '@app/modules/model/ticket';
import { Place } from '@app/modules/model/place';

import { alertService } from '@app/modules/common/alert';
import { languageSupport, languageService } from '@app/modules/common/language'
import { viewService } from '@app/modules/view';

@Component({
    selector: 'ui-cart-product',
    templateUrl:'./ui-cart.html',
    styleUrls: [ 
        './ui-cart.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiCartProduct extends languageSupport implements OnInit, OnDestroy {
    @Input('ticket') _ticket: Ticket = null;
    // Fallback para instanciaciones sin ticket (ui-cart.html:87 pasa
    // solo [place]="place" en la seccion de productos entregados).
    @Input('place') _place: Place = null;
    @Input('product') _product: CartProduct = null;

    private _subscriptions = [];

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    get IsSeparator(){
        return (this._product.status == 'SP');
    }

    private get TotalKg(){
        if (this._product.product.IsWeighted){
            return (this._product.requested[0].weight / 1000);
        }
        return null;
    }

    get Amount(){
        let _amount = this._product.requested.length; 
        if (this._product.product.IsWeighted){
            return (Math.round(_amount * this.TotalKg * 100) / 100).toFixed(2) + this.lang.tr("@kilograms");
        }
        return _amount.toString();
    }

    get ItemPrice(){
        let _price = this._product.requested[0].TotalPrice;

        let _product = this._product.product;
        if (_product.IsWeighted){
            _price = _product.MarketPrice ? this._product.requested[0].market : this._product.product.price;
        }

        return (_price || 0).toFixed(2);
    }
    
    get TotalPrice(){
        return this._product.totalprice.toFixed(2);
    }

    /** Espeja el bypass ExtendOrder de la comanda (ui-change::_GetDirect)
     *  para que carrito y cocina no diverjan. Fallback a _place cuando
     *  el template solo pasa [place] sin [ticket] (ui-cart.html:87). */
    get _extendOrder(){
        const _place = (this._ticket && this._ticket.place) || this._place || null;
        return !!(_place && _place.ExtendOrder);
    }

    private get _DisplayName(){
        if (!this._product || !this._product.product){
            // offline la relacion ticketproduct->product puede no estar resuelta
            let _req = (this._product && this._product.requested.length > 0) ? this._product.requested[0] : null;
            return _req ? _req.DisplayName : '';
        }
        if (this._extendOrder){
            return this._product.product.name;
        }
        let _requested = (this._product.requested.length > 0) ? this._product.requested[0] : null;
        if (_requested){
            return _requested.DisplayName;
        }
        return this._product.product.name;
    }

    get ProductImg(){
        if (!this._product || !this._product.product){
            let _req = (this._product && this._product.requested.length > 0) ? this._product.requested[0] : null;
            return _req ? _req.DisplayImg : null;
        }
        if (this._extendOrder){
            return this._product.product.avatar;
        }
        let _requested = (this._product.requested.length > 0) ? this._product.requested[0] : null;
        if (_requested){
            return _requested.DisplayImg;
        }
        return this._product.product.avatar;
    }

    /** Options visibles bajo el header. Si hay direct activo, solo las
     *  extras que no cubre; sino todas. En ExtendOrder tampoco filtramos. */
    get DisplayOptions(){
        if (this._extendOrder){
            return this._product.options;
        }
        let _requested = (this._product.requested.length > 0) ? this._product.requested[0] : null;
        if (_requested && _requested.ActiveDirect){
            return _requested.ExtraProductOptions;
        }
        return this._product.options;
    }

    get Requested(){    // if we do not display the innerHTML label will not refresh on safari
        return this.Amount + ' x <b>' +  this._DisplayName  + '</b>'
    }

    // Extras seleccionados en una línea (ej. "avena, sin lactosa"), para ver de un
    // vistazo cómo está modificado un producto sin desplegar. Reusa DisplayOptions
    // (que ya excluye el producto base) y devuelve null si no hay extras.
    get OptionsInline(){
        let _options = this.DisplayOptions;
        if (!_options || (_options.length == 0)){
            return null;
        }
        return _options.map(_option => _option.name).filter(Boolean).join(', ');
    }

    get ProductWeight(){
        let _totalkg = this.TotalKg;
        if (_totalkg){
            return _totalkg.toFixed(2) + this.lang.tr("@kilograms");
        }
        return null;
    }

    get ProductPrice(){
        let _price = (this._product.product.MarketPrice) ? this._product.requested[0].market : this._product.product.price;
        if (_price == null){
            return null;
        }
        if (this._product.product.IsWeighted){
            return _price.toFixed(2) + this.lang.tr('@currency') + '/' + this.lang.tr('@kilograms') ;
        }
        return _price.toFixed(2) + this.lang.tr('@currency');
    }

    get CanAdd() {
        // we can add 'various' products
        if (this._product.product.status == 'OT'){
            return true;
        }

        // we can add if product and options are available
        if (this._product.product.status == 'AC'){
            return this._product.options.every(
            _option => {
                return _option.status == 'AC';
            });
        }

        return false;
    }

    get CanDel() {
        return (this._product.requested.length > 0);
    }

    /* expand / collapse options */

    private _showoptions = false;
    get ShowOptions(){
        return this._showoptions;
    }

    set ShowOptions(value){
        this._showoptions = value;
        this.change.markForCheck();
    }

    OnShowOptions(){
        this.ShowOptions = !this.ShowOptions;
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    constructor(private lang: languageService, private change: ChangeDetectorRef, private alertCtrl: alertService, public view: viewService) {
        super(lang, change);
    }

    ngOnInit(){
        this._subscriptions.push(
            this.view.OnTheme.subscribe(
            data => {
                this.change.markForCheck();
            })
        );    

        this._subscriptions.push(
            this._product.onRefresh.asObservable().subscribe(
            data => {
                this.change.markForCheck();
            })
        );    
    }

    ngOnDestroy(){
        super.OnDestroy();

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/

    private _sliding = false;

    async OnSliding(){
        this._sliding = true;
        setTimeout(() => {
            this._sliding = false;
        }, 250)
    }

    async OnSlideClick(event, slider){
        if (this._sliding){
            event.stopPropagation();
        }
        else {
            slider.close();
        }
    }

    @Output('onPanel') _onPanel = new EventEmitter<any>();
    OnShowProduct(){
        // Editar la ULTIMA unidad anadida (LIFO), no requested[0]: al agrupar
        // varias unidades del mismo producto (p.ej. 2 flat white ya enviados + 1
        // recien anadido), tocar la foto editaba el primero (un viejo) y lo
        // convertia en MODIFICADO. Con LIFO se edita el que acabas de anadir, que
        // se muta in-place y sigue como NUEVO. Coherente con CartDel (_OnDelProduct),
        // que ya borra el ultimo.
        let _requested = this._product.requested;
        this._onPanel.emit({
            scnd: 'opts',
            args: {
                ticketproduct: _requested[_requested.length - 1]
            }
        });
    }

    async OnAddProduct(event){
        event.stopPropagation();

        let _productinfo = {
            pritem: this._product,
            fixedp: null,
            market: this._product.requested[0].market,
            weight: this._product.requested[0].weight
        };

        if (this._product.fixed){
            _productinfo['fixedp'] = this._product.requested[0].charge;
        }

        let _ticketproduct = this._ticket.ToCart(this._product.product, this._product.options, _productinfo, this._product.comments);
        if (_ticketproduct){
            this._ticket.CartAdd(_ticketproduct);
        }
    }

    private _OnDelProduct(){
        this._ticket.CartDel(this._product.requested[this._product.requested.length - 1]);
    }

    private get _IsLastProduct(){
        let _prcount = 0;
        for(let _product of this._ticket.products){
            if (_product.product == this._product.product){
                _prcount++;
                if (_prcount > 1){
                    return false;
                }
            }
        }

        return true;    // last product in the ticket
    }

    async OnDelProduct(event){
        event.stopPropagation();

        if (this._IsLastProduct){
            const alert = this.alertCtrl.alert({
                header: this.lang.tr('@ticket_remove_product_confirm_tittle'),
                message: this.lang.tr('@ticket_remove_product_confirm_message', [ this._product.product.name ]),
                backdropDismiss: false,
                buttons: [{
                    text: this.lang.tr('@yes'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(true);
                        }, 100);
                        
                        return false;
                    }
                }, {
                    text: this.lang.tr('@no'),
                    handler: () => {
                        setTimeout(() => {
                            alert.dismiss(false);
                        }, 100);
                        return false;
                    }
                }]            
            });
    
            alert.onDidDismiss().then((detail:any) => {
                if (detail.data == true){
                    this._OnDelProduct();
                }
            });
    
            await alert.present();
        }
        else {
            this._OnDelProduct();
        }
    }

    async OnRemoveProduct(event){
        event.stopPropagation();

        let _requested = this._product.requested.slice(0);
        for (let _product of _requested){
            this._ticket.CartDel(_product);
        }
    }
}
